import { FieldArray } from 'formik'
import { AddButton, MarginSize } from '../..'
import { useFieldValue } from '../../../hooks'
import { DenseTableProps, FormTableProps, TableType } from '../types'
import { TableContent } from './table-content'

/**
 * DenseTable
 * Displays array of objects in dense, borderless (but lines between) table in desktop mode
 * and with cards when in mobile
 *
 * @property data [Object]                Array of objects of data to be iterated
 * @property recipe [Object]              Array of objects telling how the data displayed in each column
 * @param key string                      Column's key's name in data
 * @param display? string                 Optional, text to display in column's header
 * @param hideIfNoValues? boolean         Hides the header/label in table/card mode if there's no data to display on any row/card
 * @param translationid? string           Optional, id for formatMessage for translated text in column's header
 * @param displayWhenNull? string         Optional text to display when field has no value
 * @param dateFormat? string              Format for date-fns
 * @param money? boolean                  When set, value is formatted with Intl.NumberFormat
 */
export const DenseTable = <RowDataType,>({
  ...props
}: DenseTableProps<RowDataType>) => {
  if (props.type === TableType.Static) {
    return <TableContent {...props} />
  } else {
    return <EditableTableWrapper {...props} />
  }
}

export const EditableTableWrapper = <RowDataType,>({
  formProps,
  tableName,
  ...rest
}: FormTableProps<RowDataType>) => {
  const { addButtonProps } = formProps
  const value = useFieldValue(tableName)

  return (
    <FieldArray
      name={tableName}
      render={(arrayHelpers) => (
        <>
          <TableContent data={value} tableName={tableName} {...rest} />
          {addButtonProps && (
            <AddButton
              buttonDisplayType={addButtonProps.buttonDisplayType}
              marginSize={MarginSize.NONE}
              onClick={() => arrayHelpers.push(addButtonProps.newRowValue)}
              translationId={addButtonProps.translationId}
            />
          )}
        </>
      )}
    />
  )
}
